import Swup from "swup";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { initPageEffects } from "./page-effects";
import type Lenis from "lenis";

let swup: Swup | null = null;
let isTransitioning = false;

interface InitOptions {
  getLenis: () => Lenis | undefined;
}

/**
 * ページ遷移演出初期化
 */
export function initPageTransitions({ getLenis }: InitOptions) {
  if (swup) return;

  swup = new Swup({
    containers: ["#swup"],
    cache: true,
    animationSelector: false, // 独自GSAP制御
    // 手動 navigate呼び出し時(event無し)は許可し、通常クリック(eventあり)は無視
    ignoreVisit: (_url, { event }) => {
      return !!event; // クリック由来はtrueで無視 / 手動navigateはevent未定義なのでfalse
    },
    linkSelector: "a[href]", // 既定。ignoreVisitで抑止
  });

  // visit:start は一度だけ登録（重複防止）
  swup.hooks.on("visit:start", () => {});

  // オーバーレイDOM生成
  let root = document.querySelector<HTMLElement>(".transition-overlays");
  if (!root) {
    root = document.createElement("div");
    root.className = "transition-overlays";
    root.innerHTML = `
      <div class="transition-overlays__layer transition-overlays__layer--black"></div>
      <div class="transition-overlays__layer transition-overlays__layer--yellow"></div>
    `;
    document.body.appendChild(root);
  }

  const black = root.querySelector<HTMLElement>(
    ".transition-overlays__layer--black"
  )!;
  const yellow = root.querySelector<HTMLElement>(
    ".transition-overlays__layer--yellow"
  )!;

  const enterDuration = 0.3;
  const exitDuration = 0.2;
  const offset = 0.15; // 黒→黄 & 黄→黒 の開始差
  const holdDurationAfterReplace = 0.05; // コンテンツ差し替え後の待機秒数(任意で変更)

  function disableScroll() {
    const lenis = getLenis();
    lenis?.stop();
    document.documentElement.classList.add("is-transitioning");
  }

  function enableScroll() {
    const lenis = getLenis();
    lenis?.start();
    document.documentElement.classList.remove("is-transitioning");
  }

  function navigateWithTransition(url: string) {
    if (isTransitioning || !swup) return;
    isTransitioning = true;
    disableScroll();

    gsap.set([black, yellow], { transformOrigin: "left center", scaleX: 0 });

    const tlIn = gsap.timeline({ defaults: { ease: "power3.inOut" } });

    // 1 黒展開 -> 0.2s後 黄展開
    tlIn.to(black, { scaleX: 1, duration: enterDuration, force3D: true }).to(
      yellow,
      {
        scaleX: 1,
        duration: enterDuration,
        // 黄が覆い終わった瞬間にナビゲート (完全非表示状態でコンテンツ切替)
        onComplete: () => {
          swup!.navigate(url);
        },
      },
      `-=${enterDuration - offset}`
    );

    const reinitPageScripts = () => {
      const mainEl = document.querySelector<HTMLElement>("#swup");
      const page = mainEl?.dataset.page;
      initPageEffects(page);
      if (ScrollTrigger) ScrollTrigger.refresh();
    };

    const onReplaced = () => {
      enableScroll();
      reinitPageScripts();
      // 黄で覆われている間にSPメニューが開いていたら閉じ指示イベント発火
      const spHeader = document.querySelector(
        '[data-element="header-sp-container"].is-sp_menu_active'
      );
      if (spHeader) {
        window.dispatchEvent(
          new CustomEvent("page-transition:forceCloseSpMenu")
        );
      }
      isTransitioning = false;
      // 任意ホールド: 新コンテンツが黄/黒で覆われたままの時間
      setTimeout(() => {
        const tlOut = gsap.timeline({
          defaults: { ease: "power3.inOut", force3D: true },
          onComplete: () => {
            // アニメ完了後フック解除
          },
        });

        tlOut
          .set([black, yellow], { transformOrigin: "right center" })
          .to(yellow, { scaleX: 0, duration: exitDuration })
          .to(
            black,
            { scaleX: 0, duration: exitDuration, force3D: true },
            `-=${exitDuration - offset}`
          );
        swup!.hooks.off("content:replace", onReplaced as any);
      }, holdDurationAfterReplace * 1000);
    };

    // Swup v4 hooks: ページ内容差し替え後
    swup!.hooks.on("content:replace", onReplaced as any);
  }

  // 戻る/進む履歴遷移でも content:replace は発火するためここで共通再初期化
  swup.hooks.on("content:replace", () => {
    // 演出中のフック(onReplaced)で既に処理するため、演出外(popstate)のみ判定
    if (!isTransitioning) {
      const mainEl = document.querySelector<HTMLElement>("#swup");
      const page = mainEl?.dataset.page;
      initPageEffects(page);

      if (ScrollTrigger) ScrollTrigger.refresh();
      // 戻るでSPメニュー開いていたら閉じる
      const spHeader = document.querySelector(
        '[data-element="header-sp-container"].is-sp_menu_active'
      );
      if (spHeader) {
        window.dispatchEvent(
          new CustomEvent("page-transition:forceCloseSpMenu")
        );
      }
    }
  });

  // 内部リンククリックフック
  document.addEventListener("click", (e) => {
    const target = e.target as HTMLElement;
    const a = target.closest("a");
    if (!a) return;

    const href = a.getAttribute("href");
    if (!href) return;
    if (a.target === "_blank") return;
    if (a.hasAttribute("download")) return;
    if (a.dataset.noTransition === "true") return;
    if (/^https?:\/\//i.test(href) && !href.startsWith(location.origin)) return;
    if (href.startsWith("#")) return; // same-page anchor

    e.preventDefault();
    navigateWithTransition(href);
  });
}
